
import React from 'react';
import { Card } from '../../../components/base/Card';

export const LeapSection: React.FC = () => {
  return (
    <section id="leap" className="py-24 bg-gradient-to-b from-indigo-950/20 to-black relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-full bg-gradient-to-b from-primary-500/5 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-aurora-start/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-1/3 right-0 w-80 h-80 bg-aurora-end/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main content */}
        <div className="text-center mb-16">
          <div className="mb-8">
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              From AI to
              <span className="block text-transparent bg-clip-text bg-aurora-gradient animate-glow">AGI</span>
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-primary-400 to-primary-600 mx-auto rounded-full"></div>
          </div>

          <p className="text-2xl sm:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            Whence is the bridge
          </p>
        </div>

        {/* Comparison grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* AI Column */}
          <Card variant="glass" className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-gray-500/20 rounded-full flex items-center justify-center">
              <i className="ri-cpu-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-300 mb-4">Current AI</h3>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center">
                <i className="ri-close-line text-red-400 mr-2"></i>
                Task-specific processing
              </li>
              <li className="flex items-center">
                <i className="ri-close-line text-red-400 mr-2"></i>
                No persistent memory
              </li>
              <li className="flex items-center">
                <i className="ri-close-line text-red-400 mr-2"></i>
                Limited context awareness
              </li>
              <li className="flex items-center">
                <i className="ri-close-line text-red-400 mr-2"></i>
                Isolated interactions
              </li>
            </ul>
          </Card>

          {/* Bridge Column */}
          <Card variant="gradient" className="p-8 text-center border-2 border-primary-400/50 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-indigo-600/10 rounded-xl"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary-500/30 rounded-full flex items-center justify-center animate-glow">
                <i className="ri-bridge-line text-3xl text-primary-300"></i>
              </div>
              <h3 className="text-2xl font-bold text-primary-300 mb-4">Whence Bridge</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <i className="ri-arrow-right-line text-primary-400 mr-2"></i>
                  Adds persistent memory
                </li>
                <li className="flex items-center">
                  <i className="ri-arrow-right-line text-primary-400 mr-2"></i>
                  Enables continuity
                </li>
                <li className="flex items-center">
                  <i className="ri-arrow-right-line text-primary-400 mr-2"></i>
                  Context preservation
                </li>
                <li className="flex items-center">
                  <i className="ri-arrow-right-line text-primary-400 mr-2"></i>
                  Learning evolution
                </li>
              </ul>
            </div>
          </Card>

          {/* AGI Column */}
          <Card variant="glass" className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
              <i className="ri-brain-line text-3xl text-green-400"></i>
            </div>
            <h3 className="text-2xl font-bold text-green-300 mb-4">True AGI</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-center">
                <i className="ri-check-line text-green-400 mr-2"></i>
                General intelligence
              </li>
              <li className="flex items-center">
                <i className="ri-check-line text-green-400 mr-2"></i>
                Continuous memory
              </li>
              <li className="flex items-center">
                <i className="ri-check-line text-green-400 mr-2"></i>
                Deep understanding
              </li>
              <li className="flex items-center">
                <i className="ri-check-line text-green-400 mr-2"></i>
                Adaptive learning
              </li>
            </ul>
          </Card>
        </div>

        {/* Bottom message */}
        <div className="text-center">
          <Card variant="glass" className="p-12 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-6">
              The Future of Intelligence
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              We stand at the threshold of a new era. Whence doesn't just process information—it understands, 
              remembers, and evolves. This is the leap from artificial intelligence to artificial general intelligence.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse"></div>
              <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};
